/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Sohbetin yanıtlayacak uzmanını değil, çalışma kapsamını seçer.
 * Yetkili uzman kimliği ayrı, güvenilir `@` seçicisiyle taşınır.
 */
import {
  type CSSProperties,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Check,
  ChevronDown,
  Network,
  Sparkles,
  UserRound,
  Users,
  X,
} from "lucide-react";
import "./AssistantWorkModeSelector.css";

export type AssistantWorkMode =
  | "AUTO"
  | "SINGLE_EXPERT"
  | "EXPERT_BOARD"
  | "EXPERT_360";

const WORK_MODE_PANEL_MAX_WIDTH = 320;
const WORK_MODE_PANEL_VIEWPORT_GUTTER = 16;

type AssistantWorkModePanelStyle = Pick<
  CSSProperties,
  "left" | "right" | "width"
>;

type AssistantWorkModeCopy = {
  buttonLabel: string;
  autoTitle: string;
  autoDescription: string;
  singleTitle: string;
  singleDescription: string;
  expertBoardTitle: string;
  expertBoardDescription: string;
  expert360Title: string;
  expert360Description: string;
  removeExpert: string;
};

const TURKISH_COPY: AssistantWorkModeCopy = {
  buttonLabel: "Çalışma modu",
  autoTitle: "Otomatik",
  autoDescription: "QubePro soruya göre uygun yanıt yolunu seçer.",
  singleTitle: "Tek Uzman",
  singleDescription: "Yetkili bir uzman seçilir ve sohbet için sabitlenir.",
  expertBoardTitle: "Uzman Masası",
  expertBoardDescription:
    "2-5 yetkili uzmanı @ ile seçin; her uzmana kendi sorunuzu yöneltin.",
  expert360Title: "360° Analiz",
  expert360Description:
    "Tek promptu en fazla 5 ilgili yetkili uzman analiz eder; Global Asistan tek raporda birleştirir.",
  removeExpert: "Uzman yönlendirmesini kaldır",
};

const ENGLISH_COPY: AssistantWorkModeCopy = {
  buttonLabel: "Work mode",
  autoTitle: "Automatic",
  autoDescription:
    "QubePro chooses the appropriate response route for the question.",
  singleTitle: "Single expert",
  singleDescription:
    "Choose and pin one authorized expert for the conversation.",
  expertBoardTitle: "Expert desk",
  expertBoardDescription:
    "Choose 2-5 authorized experts with @ and give each expert a question.",
  expert360Title: "360° analysis",
  expert360Description:
    "Up to 5 relevant authorized experts analyze one prompt; Global Assistant consolidates one report.",
  removeExpert: "Remove expert routing",
};

function resolveCopy(language?: string): AssistantWorkModeCopy {
  return !language || language.toLowerCase().startsWith("tr")
    ? TURKISH_COPY
    : ENGLISH_COPY;
}

function modeIcon(mode: AssistantWorkMode) {
  if (mode === "SINGLE_EXPERT") return <UserRound size={15} aria-hidden />;
  if (mode === "EXPERT_BOARD") return <Users size={15} aria-hidden />;
  if (mode === "EXPERT_360") return <Network size={15} aria-hidden />;
  return <Sparkles size={15} aria-hidden />;
}

function modeTitle(
  mode: AssistantWorkMode,
  copy: AssistantWorkModeCopy,
): string {
  if (mode === "SINGLE_EXPERT") return copy.singleTitle;
  if (mode === "EXPERT_BOARD") return copy.expertBoardTitle;
  if (mode === "EXPERT_360") return copy.expert360Title;
  return copy.autoTitle;
}

type AssistantWorkModeSelectorProps = {
  mode: AssistantWorkMode;
  open: boolean;
  disabled?: boolean;
  language?: string;
  selectedAssistantName?: string | null;
  /** Dar composer yüzeylerinde açılır menünün içinde kalacağı işlem satırı. */
  panelBoundaryRef?: RefObject<HTMLElement>;
  onOpenChange: (open: boolean) => void;
  onSelectMode: (mode: AssistantWorkMode) => void;
};

export function AssistantWorkModeSelector({
  mode,
  open,
  disabled = false,
  language,
  panelBoundaryRef,
  onOpenChange,
  onSelectMode,
}: AssistantWorkModeSelectorProps) {
  const copy = resolveCopy(language);
  const rootRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] =
    useState<AssistantWorkModePanelStyle>();
  const options: Array<{
    mode: AssistantWorkMode;
    title: string;
    description: string;
  }> = [
    { mode: "AUTO", title: copy.autoTitle, description: copy.autoDescription },
    {
      mode: "SINGLE_EXPERT",
      title: copy.singleTitle,
      description: copy.singleDescription,
    },
    {
      mode: "EXPERT_BOARD",
      title: copy.expertBoardTitle,
      description: copy.expertBoardDescription,
    },
    {
      mode: "EXPERT_360",
      title: copy.expert360Title,
      description: copy.expert360Description,
    },
  ];

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onOpenChange(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onOpenChange, open]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const boundary = panelBoundaryRef?.current;
    if (!open || !root || !boundary || typeof window === "undefined") {
      setPanelStyle(undefined);
      return undefined;
    }

    const updatePanelPlacement = () => {
      const rootRect = root.getBoundingClientRect();
      const boundaryRect = boundary.getBoundingClientRect();
      const viewportAvailableWidth = Math.max(
        0,
        window.innerWidth - WORK_MODE_PANEL_VIEWPORT_GUTTER * 2,
      );
      const width = Math.min(
        WORK_MODE_PANEL_MAX_WIDTH,
        boundaryRect.width,
        viewportAvailableWidth,
      );
      if (width <= 0) return;

      const minViewportLeft = WORK_MODE_PANEL_VIEWPORT_GUTTER;
      const maxViewportLeft = Math.max(
        minViewportLeft,
        window.innerWidth - WORK_MODE_PANEL_VIEWPORT_GUTTER - width,
      );
      const preferredViewportLeft = boundaryRect.right - width;
      const viewportLeft = Math.min(
        maxViewportLeft,
        Math.max(minViewportLeft, preferredViewportLeft),
      );
      const nextStyle: AssistantWorkModePanelStyle = {
        left: viewportLeft - rootRect.left,
        right: "auto",
        width,
      };

      setPanelStyle((current) =>
        current?.left === nextStyle.left &&
        current?.right === nextStyle.right &&
        current?.width === nextStyle.width
          ? current
          : nextStyle,
      );
    };

    updatePanelPlacement();
    window.addEventListener("resize", updatePanelPlacement);

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updatePanelPlacement);
    resizeObserver?.observe(boundary);
    resizeObserver?.observe(root);

    return () => {
      window.removeEventListener("resize", updatePanelPlacement);
      resizeObserver?.disconnect();
    };
  }, [open, panelBoundaryRef]);

  const selectedTitle = modeTitle(mode, copy);
  return (
    <div className="assistant-work-mode" ref={rootRef}>
      <button
        type="button"
        className={`assistant-work-mode__button ${open ? "active" : ""}`}
        onClick={() => onOpenChange(!open)}
        disabled={disabled}
        title={`${copy.buttonLabel}: ${selectedTitle}`}
        aria-label={`${copy.buttonLabel}: ${selectedTitle}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {modeIcon(mode)}
        <span className="assistant-work-mode__button-copy">
          <span>{selectedTitle}</span>
        </span>
        <ChevronDown size={14} aria-hidden />
      </button>

      {open ? (
        <div
          className="assistant-work-mode__panel"
          role="menu"
          aria-label={copy.buttonLabel}
          style={panelStyle}
        >
          {options.map((option) => (
            <button
              key={option.mode}
              type="button"
              role="menuitemradio"
              aria-checked={mode === option.mode}
              className={`assistant-work-mode__option ${mode === option.mode ? "selected" : ""}`}
              onClick={() => {
                onOpenChange(false);
                onSelectMode(option.mode);
              }}
            >
              <span className="assistant-work-mode__option-icon">
                {modeIcon(option.mode)}
              </span>
              <span className="assistant-work-mode__option-copy">
                <strong>{option.title}</strong>
                <small>{option.description}</small>
              </span>
              {mode === option.mode ? <Check size={16} aria-hidden /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

type AssistantTargetChipProps = {
  name: string;
  disabled?: boolean;
  language?: string;
  onRemove: () => void;
};

export function AssistantTargetChip({
  name,
  disabled = false,
  language,
  onRemove,
}: AssistantTargetChipProps) {
  const copy = resolveCopy(language);
  return (
    <span
      className={`assistant-target-chip ${disabled ? "disabled" : ""}`}
      role="group"
      aria-label={`@${name}. ${copy.removeExpert}.`}
      aria-disabled={disabled}
      aria-keyshortcuts="Backspace Delete"
      tabIndex={disabled ? -1 : 0}
      onClick={(event) => {
        if (disabled) return;
        if (event.target instanceof Element && event.target.closest("button")) return;
        event.currentTarget.focus();
      }}
      onKeyDown={(event) => {
        if (disabled || (event.key !== "Backspace" && event.key !== "Delete")) return;
        event.preventDefault();
        event.stopPropagation();
        onRemove();
      }}
    >
      <strong className="assistant-target-chip__name">@{name}</strong>
      <button
        type="button"
        className="assistant-target-chip__remove"
        onClick={onRemove}
        disabled={disabled}
        title={copy.removeExpert}
        aria-label={`${copy.removeExpert}: ${name}`}
      >
        <X size={12} aria-hidden />
      </button>
    </span>
  );
}

export default AssistantWorkModeSelector;
